import { useState, useCallback } from 'react'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import moment from 'moment'
import { TableVirtuoso } from 'react-virtuoso'
import { t } from '@lingui/core/macro'

const TableHead = ({
  numberOfSelected,
  displayedData,
  handleSelectAllRows,
  requestSort,
  sortConfig,
}) => (
  <tr className="border border-t-0 border-cv-blue-200 bg-cv-blue-100">
    <th className="py-4 pl-2 text-left">
      <input
        type="checkbox"
        className="accent-[#01A166]"
        defaultChecked={numberOfSelected === displayedData?.length}
        onClick={() => handleSelectAllRows(displayedData)}
      />
    </th>
    <th onClick={() => requestSort('name')} className="py-4 pl-2 text-left uppercase">
      <span className="cursor-pointer text-cv-blue-700">
        {t`Name`}{' '}
        {sortConfig.key === 'name' ? (
          sortConfig.direction === 'ASC' ? (
            <ArrowUp size={18} className="inline-block" color="#000" />
          ) : (
            <ArrowDown size={18} className="inline-block" color="#000" />
          )
        ) : (
          ''
        )}
      </span>
    </th>
    <th onClick={() => requestSort('updated_at')} className="py-4 pl-2 text-left uppercase">
      <span className="cursor-pointer text-cv-blue-700">
        {t`Updated at`}{' '}
        {sortConfig.key === 'updated_at' ? (
          sortConfig.direction === 'ASC' ? (
            <ArrowUp size={18} className="inline-block" color="#000" />
          ) : (
            <ArrowDown size={18} className="inline-block" color="#000" />
          )
        ) : (
          ''
        )}
      </span>
    </th>
    <th className="py-4 pl-2 text-left uppercase text-cv-blue-700">{t`Type`}</th>
  </tr>
)

const EmptyPlaceholder = () =>
  Array.from({ length: 10 }).map((_, index) => (
    <tr
      key={index}
      className="border-b border-r border-b-cv-blue-200 border-r-cv-blue-200 odd:bg-cv-blue-100 animate-pulse"
    >
      <td className="py-4 px-2 text-left">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px]" />
      </td>
      <td className="p-4 min-w-[360px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
      <td className="py-4 pl-2 min-w-[140px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
      <td className="py-4 pl-2 min-w-[140px]">
        <div className="h-2 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[360px] mr-4" />
      </td>
    </tr>
  ))

const TableBody = ({ data, handleEdit, selectedRows, handleCheckboxClick }) => (
  <>
    {data?.map(item => (
      <tr
        key={item.id}
        className="border-b border-r border-b-cv-blue-200 border-r-cv-blue-200 odd:bg-cv-blue-100 cursor-pointer"
        onClick={() => handleEdit(item.id)}
      >
        <td
          className="py-4 px-2 text-left"
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <input
            type="checkbox"
            defaultChecked={selectedRows[item.id]}
            className="accent-[#01A166]"
            onClick={e => {
              e.stopPropagation()
              handleCheckboxClick(item, data, e.shiftKey)
            }}
          />
        </td>
        <td className="p-4 min-w-[360px] max-w-[360px]">
          <span className="mr-4">{item.name}</span>
          {/* <span>
                  <span className="bg-cv-primary w-[18px] h-[18px] rounded-full inline-block text-center mr-2">
                    <LoaderCircle size={12} className="inline-block" color={'#fff'} />
                  </span>
                  In Progress
                </span> */}
        </td>
        <td className="py-4 pl-2 min-w-[140px]">{moment(item.updated_at).format('MM-DD-YYYY')}</td>
        <td className="py-4 pl-2 min-w-[140px]">{item.type}</td>
      </tr>
    ))}
  </>
)

const TableFooter = ({
  pagination,
  onChangePagination,
  rowsPerPageOptions,
  startIndex,
  lists,
  totalPages,
}) => (
  <tr className="border border-cv-blue-200 border-t-cv-blue-200 bg-cv-blue-100 text-cv-blue-800 text-[10px]">
    <td colSpan={4} className="py-4 text-right pr-6">
      <span className="mr-8">{t`Rows per page:`} </span>
      <span className="mr-8">
        <select
          className="bg-cv-blue-100"
          value={pagination.perPage}
          onChange={e => {
            onChangePagination(1, parseInt(e.target.value))
          }}
        >
          {rowsPerPageOptions.map(o => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </span>
      <span className="mr-8">
        {startIndex + 1}-{Math.min(startIndex + pagination.perPage, lists?.length)} {t`of`}{' '}
        {lists?.length} {t`Rows`}
      </span>
      <span>
        <button
          className="mr-8 text-black disabled:text-[#ADBABF]"
          disabled={pagination.page === 1}
          onClick={() => onChangePagination(Math.max(pagination.page - 1, 1), pagination.perPage)}
        >
          <ChevronLeft size={18} className="inline-block" />
        </button>
        <button
          className="text-black disabled:text-[#ADBABF]"
          disabled={pagination.page === totalPages}
          onClick={() =>
            onChangePagination(Math.min(pagination.page + 1, totalPages), pagination.perPage)
          }
        >
          <ChevronRight size={18} className="inline-block" />
        </button>
      </span>
    </td>
  </tr>
)

export function ListsTable({
  lists,
  selectedRows,
  handleEdit,
  handleSelectAllRows,
  handleCheckboxClick,
  numberOfSelected,
}) {
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 50,
  })
  const [sortConfig, setSortConfig] = useState({
    key: 'updated_at',
    direction: 'DESC',
  })

  const { page, perPage } = pagination
  const rowsPerPageOptions = [50, 100, 300]
  const totalPages = lists && Math.ceil(lists.length / perPage)
  const startIndex = (page - 1) * perPage

  const onChangePagination = useCallback((newPage = page, newPerPage = perPage) => {
    setPagination({ page: newPage, perPage: newPerPage })
  }, [])

  const requestSort = useCallback(
    key => {
      let direction = `ASC`
      if (sortConfig.key === key && sortConfig.direction === 'ASC') {
        direction = 'DESC'
      }
      setSortConfig({ key, direction })
    },
    [setSortConfig, sortConfig],
  )

  const sortedLists =
    lists &&
    [...lists].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ASC' ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ASC' ? 1 : -1
      }
      return 0
    })

  const displayedData = sortedLists?.slice(startIndex, startIndex + perPage)

  return (
    <TableVirtuoso
      useWindowScroll
      fixedHeaderContent={() => (
        <TableHead
          numberOfSelected={numberOfSelected}
          displayedData={displayedData}
          handleSelectAllRows={handleSelectAllRows}
          requestSort={requestSort}
          sortConfig={sortConfig}
        />
      )}
      data={lists}
      components={{
        Table: ({ style, ...props }) => (
          <table
            className="table-fixed"
            style={{ ...style, boxShadow: `0px 4px 10px 0px rgba(0, 0, 0, 0.08)` }}
            {...props}
          />
        ),
        EmptyPlaceholder: () => (
          <tbody>
            <EmptyPlaceholder />
          </tbody>
        ),
        TableBody: () => (
          <tbody>
            <TableBody
              data={displayedData}
              handleEdit={handleEdit}
              selectedRows={selectedRows}
              handleCheckboxClick={handleCheckboxClick}
            />
          </tbody>
        ),
      }}
      fixedFooterContent={() => (
        <TableFooter
          pagination={pagination}
          onChangePagination={onChangePagination}
          rowsPerPageOptions={rowsPerPageOptions}
          startIndex={startIndex}
          lists={lists}
          totalPages={totalPages}
        />
      )}
    />
  )
}
